import React from 'react';
import { OneWordRecord } from '@/services/oneWordRecordsService';

interface Props {
  word: string;
  bestLapMs: number;       // session best clean lap
  currentStreak: number;   // ongoing clean-finish streak ("Best Flow")
  bestStreak: number;      // session best streak
  lapsCompleted: number;   // total finished laps ("Best Race")
  record: OneWordRecord | null;
  letterMistakes: Record<string, number>; // live, this race
}

const fmtMs = (ms: number): string => {
  if (!ms || !isFinite(ms) || ms <= 0) return '—';
  if (ms < 1000) return `${Math.round(ms)} ms`;
  return `${(ms / 1000).toFixed(2)} s`;
};

const fmtLetter = (l: string): string => (l === '_' ? '␣' : l);

const Cell: React.FC<{
  label: string;
  value: string;
  sub?: string;
  record?: string;
  highlight?: boolean;
}> = ({ label, value, sub, record, highlight }) => (
  <div className={`owm-cell ${highlight ? 'owm-cell-highlight' : ''}`}>
    <div className="owm-label">{label}</div>
    <div className="owm-value">{value}</div>
    {sub && <div className="owm-sub">{sub}</div>}
    {record !== undefined && <div className="owm-record">Record: {record}</div>}
  </div>
);

const OneWordMetricsHUD: React.FC<Props> = ({
  word,
  bestLapMs,
  currentStreak,
  bestStreak,
  lapsCompleted,
  record,
  letterMistakes,
}) => {
  const bestLapBeatsRecord = record && bestLapMs > 0 && (!record.bestLapMs || bestLapMs < record.bestLapMs);
  const flowBeatsRecord = record && bestStreak > (record.bestStreak || 0);

  const mistakes = Object.entries(letterMistakes || {})
    .filter(([, count]) => count > 0)
    .sort((a, b) => b[1] - a[1]);

  return (
    <div className="one-word-metrics-hud">
      <div className="owm-word">"{word}"</div>

      <div className="owm-grid">
        <Cell
          label="Best Lap"
          value={fmtMs(bestLapMs)}
          record={record ? fmtMs(record.bestLapMs) : '—'}
          highlight={!!bestLapBeatsRecord}
        />
        <Cell
          label="Best Flow"
          value={String(currentStreak)}
          sub={`best ${bestStreak}`}
          record={record ? String(record.bestStreak ?? 0) : '—'}
          highlight={!!flowBeatsRecord}
        />
        <Cell
          label="Best Race"
          value={String(lapsCompleted)}
          record={record ? String(record.totalLaps ?? 0) : '—'}
        />
      </div>

      <div className="owm-mistakes">
        <div className="owm-mistakes-title">Mistaken Letters</div>
        {mistakes.length === 0 ? (
          <div className="owm-mistakes-empty">No mistakes yet — keep it clean!</div>
        ) : (
          <div className="owm-mistakes-list">
            {mistakes.map(([letter, count]) => (
              <div key={letter} className="owm-mistake-row">
                <span className="owm-mistake-letter">{fmtLetter(letter)}</span>
                <span className="owm-mistake-count">{count}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      <style>{`
        .one-word-metrics-hud {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 6px;
          padding: 8px 12px;
          background: rgba(17, 24, 39, 0.72);
          border: 1px solid rgba(139, 92, 246, 0.4);
          border-radius: 10px;
          backdrop-filter: blur(6px);
        }
        .owm-word {
          font-size: 0.8rem;
          color: #cbd5e1;
          letter-spacing: 0.04em;
        }
        .owm-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 10px;
          justify-items: center;
        }
        .owm-cell {
          display: flex;
          flex-direction: column;
          align-items: center;
          min-width: 78px;
          padding: 3px 8px;
          border-radius: 6px;
        }
        .owm-cell-highlight {
          background: rgba(34, 197, 94, 0.18);
          border: 1px solid rgba(34, 197, 94, 0.5);
        }
        .owm-label {
          font-size: 0.6rem;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.06em;
        }
        .owm-value {
          font-size: 0.95rem;
          color: #f8fafc;
          font-weight: 700;
          font-variant-numeric: tabular-nums;
        }
        .owm-sub {
          font-size: 0.6rem;
          color: #94a3b8;
        }
        .owm-record {
          font-size: 0.62rem;
          color: #64748b;
          font-variant-numeric: tabular-nums;
        }
        .owm-mistakes {
          width: 100%;
          margin-top: 2px;
          padding-top: 6px;
          border-top: 1px solid rgba(75, 85, 99, 0.35);
        }
        .owm-mistakes-title {
          font-size: 0.6rem;
          color: #94a3b8;
          text-transform: uppercase;
          letter-spacing: 0.06em;
          text-align: center;
          margin-bottom: 4px;
        }
        .owm-mistakes-empty {
          font-size: 0.62rem;
          color: #64748b;
          text-align: center;
        }
        .owm-mistakes-list {
          display: flex;
          flex-direction: column;
          gap: 2px;
          max-height: 96px;
          overflow-y: auto;
        }
        .owm-mistake-row {
          display: flex;
          justify-content: space-between;
          align-items: center;
          padding: 1px 8px;
          border-radius: 4px;
          background: rgba(239, 68, 68, 0.08);
        }
        .owm-mistake-letter {
          font-size: 0.78rem;
          font-weight: 700;
          color: #f8fafc;
          font-variant-numeric: tabular-nums;
        }
        .owm-mistake-count {
          font-size: 0.78rem;
          font-weight: 700;
          color: #f87171;
          font-variant-numeric: tabular-nums;
        }
      `}</style>
    </div>
  );
};

export default React.memo(OneWordMetricsHUD);
